/**
 * Sticky Add to cart bar, mobile only.
 *
 * The button is not a second form: it carries form="<the real form id>", so a click fires
 * the real form's submit event and product-form.js handles it exactly as it handles the
 * button inside the form. Its label, price and state are mirrored from the real button, so
 * variant changes and the loading state need no wiring of their own.
 */
if (!customElements.get('sticky-atc')) {

class StickyAtc extends HTMLElement {
    connectedCallback() {
        this.button = this.querySelector('.sticky-atc__button');
        this.panel = this.querySelector('.sticky-atc__panel');
        this.toggle = this.querySelector('.sticky-atc__toggle');
        this.source = document.getElementById(this.dataset.sourceId);

        if (!this.button || !this.source) return;

        this.controller?.abort();
        this.controller = new AbortController();
        const { signal } = this.controller;

        this.sync();
        this.buildPanel();
        this.fillPlainSummary();
        this.watchWidget();

        // Delegated: there are two toggles -- the one in the row and the copy the panel grid
        // gets -- and the second one is created after this runs.
        this.addEventListener('click', (event) => {
            if (event.target.closest('.sticky-atc__change')) {
                this.revealWidget();
                return;
            }

            if (!event.target.closest('.sticky-atc__toggle')) return;

            // Gates the closing animation, so the arrow is still on first paint.
            this.classList.add('has-toggled');

            const open = this.classList.toggle('is-open');
            this.querySelectorAll('.sticky-atc__toggle').forEach((toggle) => {
                toggle.setAttribute('aria-expanded', open);
            });
        }, { signal });

        // Only the source is observed, so writing into this.button cannot re-trigger this.
        this.mutationObserver = new MutationObserver(() => this.sync());
        this.mutationObserver.observe(this.source, {
            attributes: true,
            childList: true,
            subtree: true,
            characterData: true,
        });

        this.intersectionObserver = new IntersectionObserver(([entry]) => {
            this.classList.toggle('is-visible', !entry.isIntersecting);
        });

        // Observing on the next frame, not straight away: the observer delivers its first
        // entry almost immediately, and if is-visible lands before the browser has committed
        // the bar's starting style, there is no value to animate from and it appears with a
        // jump. One frame is enough for translateY(100%)/opacity:0 to be the current style.
        requestAnimationFrame(() => this.intersectionObserver.observe(this.source));
    }

    disconnectedCallback() {
        this.controller?.abort();
        this.mutationObserver?.disconnect();
        this.intersectionObserver?.disconnect();
        clearTimeout(this.widgetTimer);
    }

    // Everything is looked up inside this product only. A flavor swap leaves the outgoing
    // markup in the document for half a second, so a document-wide query would find the
    // previous product's widget and subtitle.
    get productInfo() {
        return this.closest('product-info');
    }

    get widget() {
        return this.productInfo?.querySelector('recharge-subscription-widget') || null;
    }

    /**
     * Change takes the visitor to the purchase options, which are in the Recharge widget up
     * in the page -- the bar has no way of setting them itself, the widget owns that state.
     */
    revealWidget() {
        const widget = this.widget;
        if (!widget) return;

        // The panel is only ever open over the summary, but a stray open state would leave
        // the bar covering what it just scrolled to.
        this.classList.remove('is-open');
        this.querySelectorAll('.sticky-atc__toggle').forEach((toggle) => {
            toggle.setAttribute('aria-expanded', 'false');
        });

        // Centred rather than at the top: the bar is fixed to the bottom of the viewport and
        // would otherwise sit over the options it is pointing at.
        widget.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    /**
     * A product with no subscriptions has no purchase option to report, so the summary line
     * carries the Buy Buttons subtitle instead. That setting belongs to the other block, so
     * it is copied from the rendered markup; with nothing to copy the line is dropped.
     */
    fillPlainSummary() {
        const plain = this.querySelector('.sticky-atc__summary-text--plain');
        if (!plain) return;

        const subtitle = this.productInfo?.querySelector('.product-form__subtitle');
        const text = subtitle ? subtitle.textContent.replace(/\s+/g, ' ').trim() : '';

        plain.textContent = text;
        this.classList.toggle('has-no-summary', !text);
    }

    /**
     * The markup starts from the product's selling plan groups, which is right in every case
     * seen so far. The widget is still watched for: it is rendered by an app, so it can be
     * missing on a product that has the groups, and it settles late either way.
     */
    watchWidget() {
        clearTimeout(this.widgetTimer);

        const deadline = performance.now() + 15000;

        const tick = () => {
            // An empty widget is a product without subscriptions rather than a slow render,
            // so only one with purchase options in it counts.
            if (this.widget?.shadowRoot?.querySelector('.rc-purchase-option')) {
                this.dataset.hasSubscription = 'true';
                return;
            }

            if (performance.now() >= deadline) {
                this.dataset.hasSubscription = 'false';
                return;
            }

            this.widgetTimer = setTimeout(tick, 250);
        };

        tick();
    }

    /**
     * Fills the flavor panel with a copy of the alt variant selector from the page. Cloning
     * keeps a single source of truth for the collection setting, and the copy upgrades into
     * a working <alt-variant-selector> on insertion, so a tap still runs the product swap.
     */
    buildPanel() {
        const source = Array.from(document.querySelectorAll('alt-variant-selector'))
            .find((element) => !element.closest('sticky-atc'));

        if (!source || !this.panel) {
            this.toggle?.remove();
            return;
        }

        const copy = source.cloneNode(true);
        // The originals stay in the document, and product-info.js looks tabs up by id.
        copy.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));

        // Open, the whole collapsed row is hidden, so the grid carries its own toggle. Adding
        // it as a list item lets it sit in the last row of the grid as a real cell instead of
        // being positioned against it.
        const list = copy.querySelector('.alt-variant-selector__list');

        if (list) {
            const cell = document.createElement('li');
            cell.className = 'sticky-atc__toggle-cell';
            cell.appendChild(this.toggle.cloneNode(true));
            list.appendChild(cell);
        }

        this.panel.replaceChildren(copy);
    }

    sync() {
        this.button.innerHTML = this.source.innerHTML;
        this.button.classList.toggle('loading', this.source.classList.contains('loading'));

        ['disabled', 'aria-disabled', 'bis'].forEach((name) => {
            const value = this.source.getAttribute(name);

            if (value === null) {
                this.button.removeAttribute(name);
            } else {
                this.button.setAttribute(name, value);
            }
        });
    }
}

customElements.define('sticky-atc', StickyAtc);

}
